import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { AnalyticsApi } from '../../services/api/AnalyticsApi';
import {
  MetricCard,
  LoadingChart,
  EmptyState,
  DateRangePicker,
} from '../../components/analytics/SharedAnalyticsComponents';
import PdfExportButton from '../../components/analytics/PdfExportButton';
import { ReportGenerator } from '../../utils/ReportGenerator';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const COLORS = ['#007bff', '#28a745', '#ffc107', '#dc3545', '#17a2b8', '#6f42c1', '#20c997', '#fd7e14']; // eslint-disable-line no-unused-vars
const FUNNEL_COLORS = ['#007bff', '#28a745', '#ffc107', '#dc3545'];

const TABS = [
  { key: 'funnel', label: 'Study Funnel' },
  { key: 'documents', label: 'Document Engagement' },
  { key: 'features', label: 'Feature Adoption' },
  { key: 'sessions', label: 'Learning Sessions' },
  { key: 'profiles', label: 'User Profiles' }
];

const LEARNING_STYLE_COLORS = {
  quiz_heavy: '#007bff',
  chat_heavy: '#28a745',
  reading_heavy: '#ffc107',
  balanced: '#17a2b8'
};

/**
 * LearningIntelligenceDashboard - Phase 4 unified analytics dashboard.
 * Surfaces document engagement, study funnel, feature adoption,
 * learning session analytics, and user learning profiles.
 */
export default function LearningIntelligenceDashboard() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('funnel');
  const [dateRange, setDateRange] = useState(() => {
    const endDate = new Date();
    const startDate = new Date(Date.now() - 30 * 86400000);
    return { from: startDate.toISOString(), to: endDate.toISOString() };
  });

  const [funnel, setFunnel] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [features, setFeatures] = useState([]);
  const [, setFeatureTimeSeries] = useState([]);
  const [sessions, setSessions] = useState({ sessions: [], stats: {} });
  const [profiles, setProfiles] = useState({ profiles: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const dashboardRef = useRef(null);

  const handlePdfExport = useCallback(async () => {
    const report = new ReportGenerator('Learning Intelligence Report', dateRange);
    const metrics = [];
    if (funnel) {
      metrics.push({ label: 'Funnel Conversion', value: `${(funnel.overallConversionRate != null ? (funnel.overallConversionRate <= 1 ? (funnel.overallConversionRate * 100) : funnel.overallConversionRate) : 0).toFixed(1)}%` });
    }
    if (features.length) {
      const topFeature = features[0];
      metrics.push({ label: 'Top Feature', value: topFeature.feature || topFeature.eventType || '', sublabel: `${topFeature.uniqueUsers || 0} users` });
    }
    if (sessions.stats) {
      metrics.push({ label: 'Avg Session', value: sessions.stats.avgDurationSeconds ? `${Math.floor(sessions.stats.avgDurationSeconds / 60)}m` : 'N/A' });
    }
    if (profiles.stats) {
      metrics.push({ label: 'Total Learners', value: String(profiles.stats.totalProfiles || profiles.profiles?.length || 0) });
    }
    if (metrics.length) report.addMetrics(metrics);

    if (dashboardRef.current) {
      report.addSectionHeading('Dashboard Overview');
      await report.addChartFromRef(dashboardRef, 'Learning Intelligence dashboard charts', { maxHeight: 180 });
    }

    if (documents.length) {
      report.addSectionHeading('Document Engagement');
      report.addTable(
        ['Document', 'Opens', 'Chat Prompts', 'Quizzes', 'Avg Quiz Score'],
        documents.slice(0, 20).map(d => [
          d.documentTitle || d.documentId || '', d.opens || 0, d.chatPrompts || 0,
          d.quizCompletions || 0, d.avgQuizScore != null ? `${Number(d.avgQuizScore).toFixed(0)}%` : '\u2014'
        ])
      );
    }

    if (features.length) {
      report.addSectionHeading('Feature Adoption');
      report.addTable(
        ['Feature', 'Unique Users', 'Total Events', 'Adoption %'],
        features.map(f => [
          f.feature || f.eventType || '', f.uniqueUsers || 0, f.totalEvents || 0,
          f.adoptionPct != null ? `${Number(f.adoptionPct).toFixed(1)}%` : '\u2014'
        ])
      );
    }

    report.download('learning-intelligence');
  }, [funnel, documents, features, sessions, profiles, dateRange]);

  // Profiles filters
  const [profileFilters, setProfileFilters] = useState({
    learningStyle: '',
    subscriptionStatus: '',
    sortBy: 'engagementScore',
    sortDir: 'DESC',
    page: 0
  });

  // Sessions filters
  const [sessionFilters, setSessionFilters] = useState({
    userId: '',
    page: 0
  });

  const isAdmin = user?.roles?.some(r => r.roleName === 'admin');

  const loadTabData = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    setError(null);

    try {
      const filters = { from: dateRange.from, to: dateRange.to };

      switch (activeTab) {
        case 'funnel': {
          const data = await AnalyticsApi.getStudyFunnel(filters);
          setFunnel(data);
          break;
        }
        case 'documents': {
          const data = await AnalyticsApi.getDocumentEngagement({ ...filters, topN: 25 });
          setDocuments(data);
          break;
        }
        case 'features': {
          const [summary, ts] = await Promise.all([
            AnalyticsApi.getFeatureAdoption({ ...filters, timeSeries: false }),
            AnalyticsApi.getFeatureAdoption({ ...filters, timeSeries: true })
          ]);
          setFeatures(summary);
          setFeatureTimeSeries(ts);
          break;
        }
        case 'sessions': {
          const data = await AnalyticsApi.getLearningSessionAnalytics({
            ...filters,
            userId: sessionFilters.userId || undefined,
            page: sessionFilters.page,
            pageSize: 15
          });
          setSessions(data);
          break;
        }
        case 'profiles': {
          const data = await AnalyticsApi.getUserLearningProfiles({
            ...profileFilters,
            learningStyle: profileFilters.learningStyle || undefined,
            subscriptionStatus: profileFilters.subscriptionStatus || undefined,
            pageSize: 15
          });
          setProfiles(data);
          break;
        }
        default:
          break;
      }
    } catch (err) {
      setError(err);
      console.error(`Failed to load ${activeTab} data:`, err);
    } finally {
      setLoading(false);
    }
  }, [dateRange, activeTab, isAdmin, sessionFilters, profileFilters]);

  useEffect(() => {
    loadTabData();
  }, [loadTabData]);

  if (!isAdmin) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
        <h2>Access Denied</h2>
        <p>Admin access required to view analytics.</p>
      </div>
    );
  }

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0m';
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
    return `${(seconds / 3600).toFixed(1)}h`;
  };

  const formatNumber = (n) => {
    if (n == null) return '—';
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return String(n);
  };

  // ─── Study Funnel Tab ─────────────────────────────────────────────

  const renderFunnel = () => {
    if (loading) return <LoadingChart height={400} />;
    if (!funnel || !funnel.steps?.length) return <EmptyState message="No funnel data for this period" />;

    const chartData = funnel.steps.map((step, i) => ({
      name: step.name,
      value: step.count,
      fill: FUNNEL_COLORS[i]
    }));

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        {/* KPI Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
          {funnel.steps.map((step, i) => (
            <MetricCard
              key={step.eventType}
              label={step.name}
              value={formatNumber(step.count)}
              subtitle={`${step.conversionRate != null ? step.conversionRate : 0}% conversion`}
              color={FUNNEL_COLORS[i]}
            />
          ))}
        </div>

        {/* Funnel Chart */}
        <div style={cardStyle}>
          <h3 style={chartTitleStyle}>Study Conversion Funnel</h3>
          <p style={{ fontSize: '.8rem', color: 'var(--text-muted)', margin: '0 0 1rem' }}>
            Tracks how users progress: Open Document → Chat with AI → Start Quiz → Complete Quiz
          </p>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData} layout="vertical" barCategoryGap="20%">
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" />
              <XAxis type="number" tick={{ fontSize: 12 }} />
              <YAxis dataKey="name" type="category" width={140} tick={{ fontSize: 12 }} />
              <Tooltip
                formatter={(val) => [formatNumber(val), 'Users']}
                contentStyle={{ background: 'var(--card-bg)', border: '1px solid var(--border-light)', borderRadius: '8px' }}
              />
              <Bar dataKey="value" radius={[0, 8, 8, 0]}>
                {chartData.map((entry, i) => (
                  <Cell key={i} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          {funnel.overallConversionRate != null && (
            <div style={{ textAlign: 'center', marginTop: '0.5rem', fontSize: '.85rem', color: 'var(--text-secondary)' }}>
              Overall Conversion Rate: <strong>{(funnel.overallConversionRate <= 1 ? (funnel.overallConversionRate * 100).toFixed(1) : Number(funnel.overallConversionRate).toFixed(1))}%</strong>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ─── Document Engagement Tab ──────────────────────────────────────

  const renderDocuments = () => {
    if (loading) return <LoadingChart height={400} />;
    if (!documents.length) return <EmptyState message="No document engagement data for this period" />;

    const topDocs = documents.slice(0, 10);

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        {/* Summary cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
          <MetricCard
            label="Documents Tracked"
            value={documents.length}
            color="#007bff"
          />
          <MetricCard
            label="Total Opens"
            value={formatNumber(documents.reduce((s, d) => s + (d.opens || 0), 0))}
            color="#28a745"
          />
          <MetricCard
            label="Chat Prompts"
            value={formatNumber(documents.reduce((s, d) => s + (d.chatPrompts || 0), 0))}
            color="#ffc107"
          />
          <MetricCard
            label="Quizzes Completed"
            value={formatNumber(documents.reduce((s, d) => s + (d.quizCompletions || 0), 0))}
            color="#dc3545"
          />
        </div>

        {/* Bar chart */}
        <div style={cardStyle}>
          <h3 style={chartTitleStyle}>Top Documents by Engagement</h3>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={topDocs} layout="vertical" barCategoryGap="15%">
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis
                dataKey="documentId"
                type="category"
                width={180}
                tick={{ fontSize: 11 }}
                tickFormatter={(v) => v?.length > 24 ? v.slice(0, 24) + '…' : v}
              />
              <Tooltip contentStyle={{ background: 'var(--card-bg)', border: '1px solid var(--border-light)', borderRadius: '8px' }} />
              <Legend />
              <Bar dataKey="opens" name="Opens" fill="#007bff" stackId="a" />
              <Bar dataKey="chatPrompts" name="Chats" fill="#28a745" stackId="a" />
              <Bar dataKey="quizCompletions" name="Quizzes" fill="#dc3545" stackId="a" />
              <Bar dataKey="flashcardSessions" name="Flashcards" fill="#ffc107" stackId="a" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Table */}
        <div style={cardStyle}>
          <h3 style={chartTitleStyle}>Document Engagement Detail</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Document ID</th>
                  <th style={thStyle}>Opens</th>
                  <th style={thStyle}>Chats</th>
                  <th style={thStyle}>Quiz Starts</th>
                  <th style={thStyle}>Quiz Completions</th>
                  <th style={thStyle}>Flashcards</th>
                  <th style={thStyle}>Unique Users</th>
                  <th style={thStyle}>Avg Quiz Score</th>
                </tr>
              </thead>
              <tbody>
                {documents.map((doc, i) => (
                  <tr key={i} style={i % 2 === 0 ? {} : { background: 'var(--table-stripe)' }}>
                    <td style={tdStyle} title={doc.documentId}>{doc.documentId?.slice(0, 30)}{doc.documentId?.length > 30 ? '…' : ''}</td>
                    <td style={tdNumStyle}>{doc.opens || 0}</td>
                    <td style={tdNumStyle}>{doc.chatPrompts || 0}</td>
                    <td style={tdNumStyle}>{doc.quizStarts || 0}</td>
                    <td style={tdNumStyle}>{doc.quizCompletions || 0}</td>
                    <td style={tdNumStyle}>{doc.flashcardSessions || 0}</td>
                    <td style={tdNumStyle}>{doc.uniqueUsers || 0}</td>
                    <td style={tdNumStyle}>{doc.avgQuizScore != null ? Number(doc.avgQuizScore).toFixed(1) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // ─── Feature Adoption Tab ────────────────────────────────────────

  const renderFeatures = () => {
    if (loading) return <LoadingChart height={400} />;
    if (!features.length) return <EmptyState message="No feature adoption data for this period" />;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        {/* Summary */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
          <MetricCard label="Features Tracked" value={features.length} color="#007bff" />
          <MetricCard label="Total Events" value={formatNumber(features.reduce((s, f) => s + (f.totalEvents || 0), 0))} color="#28a745" />
          <MetricCard label="Most Adopted" value={features[0]?.feature || '—'} subtitle={`${features[0]?.adoptionPct || 0}% of users`} color="#ffc107" />
        </div>

        {/* Adoption bar chart */}
        <div style={cardStyle}>
          <h3 style={chartTitleStyle}>Feature Adoption by Unique Users</h3>
          <ResponsiveContainer width="100%" height={Math.max(300, features.length * 35)}>
            <BarChart data={features} layout="vertical" barCategoryGap="12%">
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis dataKey="feature" type="category" width={200} tick={{ fontSize: 11 }} />
              <Tooltip
                formatter={(val, name) => [formatNumber(val), name === 'uniqueUsers' ? 'Unique Users' : 'Total Events']}
                contentStyle={{ background: 'var(--card-bg)', border: '1px solid var(--border-light)', borderRadius: '8px' }}
              />
              <Legend />
              <Bar dataKey="uniqueUsers" name="Unique Users" fill="#007bff" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Adoption % table */}
        <div style={cardStyle}>
          <h3 style={chartTitleStyle}>Feature Adoption Rates</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Feature</th>
                  <th style={thStyle}>Unique Users</th>
                  <th style={thStyle}>Total Events</th>
                  <th style={thStyle}>Adoption %</th>
                  <th style={thStyle}>Avg Events/User</th>
                </tr>
              </thead>
              <tbody>
                {features.map((f, i) => (
                  <tr key={i} style={i % 2 === 0 ? {} : { background: 'var(--table-stripe)' }}>
                    <td style={tdStyle}>{f.feature}</td>
                    <td style={tdNumStyle}>{f.uniqueUsers || 0}</td>
                    <td style={tdNumStyle}>{formatNumber(f.totalEvents || 0)}</td>
                    <td style={tdNumStyle}>{f.adoptionPct != null ? `${f.adoptionPct}%` : '—'}</td>
                    <td style={tdNumStyle}>{f.uniqueUsers ? ((f.totalEvents || 0) / f.uniqueUsers).toFixed(1) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // ─── Learning Sessions Tab ───────────────────────────────────────

  const renderSessions = () => {
    if (loading) return <LoadingChart height={400} />;
    const stats = sessions.stats || {};
    const rows = sessions.sessions || [];

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem' }}>
          <MetricCard label="Total Sessions" value={formatNumber(stats.totalSessions)} color="#007bff" />
          <MetricCard label="Unique Users" value={formatNumber(stats.uniqueUsers)} color="#28a745" />
          <MetricCard label="Avg Duration" value={formatDuration(stats.avgDurationSeconds)} color="#ffc107" />
          <MetricCard label="Total Study Time" value={formatDuration(stats.totalStudyTimeSeconds)} color="#dc3545" />
          <MetricCard label="Avg Quiz Score" value={stats.avgQuizScore != null ? Number(stats.avgQuizScore).toFixed(1) : '—'} color="#17a2b8" />
          <MetricCard label="Avg Events/Session" value={stats.avgEventCount != null ? Number(stats.avgEventCount).toFixed(1) : '—'} color="#6f42c1" />
        </div>

        {/* User filter */}
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Filter by User ID..."
            value={sessionFilters.userId}
            onChange={(e) => setSessionFilters(prev => ({ ...prev, userId: e.target.value, page: 0 }))}
            style={inputStyle}
          />
          {sessionFilters.userId && (
            <button onClick={() => setSessionFilters(prev => ({ ...prev, userId: '', page: 0 }))} style={clearBtnStyle}>
              Clear
            </button>
          )}
        </div>

        {/* Sessions table */}
        <div style={cardStyle}>
          <h3 style={chartTitleStyle}>Learning Sessions</h3>
          {rows.length === 0 ? (
            <EmptyState message="No sessions found for this period" />
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>User</th>
                      <th style={thStyle}>Started</th>
                      <th style={thStyle}>Duration</th>
                      <th style={thStyle}>Events</th>
                      <th style={thStyle}>Pages</th>
                      <th style={thStyle}>Docs</th>
                      <th style={thStyle}>Chats</th>
                      <th style={thStyle}>Quizzes</th>
                      <th style={thStyle}>Flashcards</th>
                      <th style={thStyle}>Quiz Score</th>
                      <th style={thStyle}>Category</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((s, i) => (
                      <tr key={s.sessionId || i} style={i % 2 === 0 ? {} : { background: 'var(--table-stripe)' }}>
                        <td style={tdStyle}>{s.userId || '—'}</td>
                        <td style={tdStyle}>{formatDate(s.startedAt)}</td>
                        <td style={tdNumStyle}>{formatDuration(s.durationSeconds)}</td>
                        <td style={tdNumStyle}>{s.eventCount || 0}</td>
                        <td style={tdNumStyle}>{s.pagesViewed || 0}</td>
                        <td style={tdNumStyle}>{s.documentsOpened || 0}</td>
                        <td style={tdNumStyle}>{s.chatPromptsSent || 0}</td>
                        <td style={tdNumStyle}>{s.quizzesCompleted || 0}</td>
                        <td style={tdNumStyle}>{s.flashcardSessions || 0}</td>
                        <td style={tdNumStyle}>{s.avgQuizScore != null ? Number(s.avgQuizScore).toFixed(1) : '—'}</td>
                        <td style={tdStyle}>{s.primaryCategory || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Pagination */}
              <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginTop: '1rem' }}>
                <button
                  onClick={() => setSessionFilters(prev => ({ ...prev, page: Math.max(0, prev.page - 1) }))}
                  disabled={sessionFilters.page === 0}
                  style={pageBtnStyle}
                >
                  ← Previous
                </button>
                <span style={{ padding: '0.5rem', fontSize: '.85rem', color: 'var(--text-muted)' }}>
                  Page {sessionFilters.page + 1}
                </span>
                <button
                  onClick={() => setSessionFilters(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={rows.length < 15}
                  style={pageBtnStyle}
                >
                  Next →
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  // ─── User Profiles Tab ───────────────────────────────────────────

  const renderProfiles = () => {
    if (loading) return <LoadingChart height={400} />;
    const stats = profiles.stats || {};
    const rows = profiles.profiles || [];

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem' }}>
          <MetricCard label="Total Users" value={formatNumber(stats.totalUsers)} color="#007bff" />
          <MetricCard label="Avg Sessions" value={stats.avgSessions != null ? Number(stats.avgSessions).toFixed(1) : '—'} color="#28a745" />
          <MetricCard label="Avg Study Time" value={formatDuration(stats.avgStudyTimeSeconds)} color="#ffc107" />
          <MetricCard label="Avg Quiz Score" value={stats.avgQuizScore != null ? Number(stats.avgQuizScore).toFixed(1) : '—'} color="#dc3545" />
          <MetricCard label="Avg Engagement" value={stats.avgEngagementScore != null ? Number(stats.avgEngagementScore).toFixed(0) : '—'} color="#17a2b8" />
          <MetricCard label="Avg Days Inactive" value={stats.avgDaysSinceActivity != null ? Number(stats.avgDaysSinceActivity).toFixed(0) : '—'} color="#6f42c1" />
        </div>

        {/* Learning style breakdown */}
        {(stats.quizHeavyCount || stats.chatHeavyCount || stats.readingHeavyCount || stats.balancedCount) && (
          <div style={cardStyle}>
            <h3 style={chartTitleStyle}>Learning Style Distribution</h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={[
                    { name: 'Quiz Heavy', value: Number(stats.quizHeavyCount) || 0 },
                    { name: 'Chat Heavy', value: Number(stats.chatHeavyCount) || 0 },
                    { name: 'Reading Heavy', value: Number(stats.readingHeavyCount) || 0 },
                    { name: 'Balanced', value: Number(stats.balancedCount) || 0 }
                  ].filter(d => d.value > 0)}
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  innerRadius={50}
                  paddingAngle={3}
                  dataKey="value"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                >
                  <Cell fill={LEARNING_STYLE_COLORS.quiz_heavy} />
                  <Cell fill={LEARNING_STYLE_COLORS.chat_heavy} />
                  <Cell fill={LEARNING_STYLE_COLORS.reading_heavy} />
                  <Cell fill={LEARNING_STYLE_COLORS.balanced} />
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Filters */}
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <select
            value={profileFilters.learningStyle}
            onChange={(e) => setProfileFilters(prev => ({ ...prev, learningStyle: e.target.value, page: 0 }))}
            style={inputStyle}
          >
            <option value="">All Learning Styles</option>
            <option value="quiz_heavy">Quiz Heavy</option>
            <option value="chat_heavy">Chat Heavy</option>
            <option value="reading_heavy">Reading Heavy</option>
            <option value="balanced">Balanced</option>
          </select>
          <select
            value={profileFilters.sortBy}
            onChange={(e) => setProfileFilters(prev => ({ ...prev, sortBy: e.target.value, page: 0 }))}
            style={inputStyle}
          >
            <option value="engagementScore">Sort by Engagement</option>
            <option value="totalSessions">Sort by Sessions</option>
            <option value="totalStudyTimeSeconds">Sort by Study Time</option>
            <option value="avgQuizScore">Sort by Quiz Score</option>
            <option value="totalAiCostUsd">Sort by AI Cost</option>
            <option value="daysSinceLastActivity">Sort by Last Active</option>
            <option value="documentsProcessed">Sort by Documents</option>
          </select>
          <button
            onClick={() => setProfileFilters(prev => ({ ...prev, sortDir: prev.sortDir === 'DESC' ? 'ASC' : 'DESC', page: 0 }))}
            style={clearBtnStyle}
          >
            {profileFilters.sortDir === 'DESC' ? '↓ DESC' : '↑ ASC'}
          </button>
        </div>

        {/* Profiles table */}
        <div style={cardStyle}>
          <h3 style={chartTitleStyle}>User Learning Profiles</h3>
          {rows.length === 0 ? (
            <EmptyState message="No user profiles available. Run the refresh job to populate." />
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>User ID</th>
                      <th style={thStyle}>Sessions</th>
                      <th style={thStyle}>Study Time</th>
                      <th style={thStyle}>Docs</th>
                      <th style={thStyle}>Quizzes</th>
                      <th style={thStyle}>Quiz Score</th>
                      <th style={thStyle}>AI Cost</th>
                      <th style={thStyle}>AI Prompts</th>
                      <th style={thStyle}>Engagement</th>
                      <th style={thStyle}>Days Inactive</th>
                      <th style={thStyle}>Style</th>
                      <th style={thStyle}>Subscription</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((p, i) => (
                      <tr key={p.userId || i} style={i % 2 === 0 ? {} : { background: 'var(--table-stripe)' }}>
                        <td style={tdStyle}>{p.userId}</td>
                        <td style={tdNumStyle}>{p.totalSessions || 0}</td>
                        <td style={tdNumStyle}>{formatDuration(p.totalStudyTimeSeconds)}</td>
                        <td style={tdNumStyle}>{p.documentsProcessed || 0}</td>
                        <td style={tdNumStyle}>{p.totalQuizzesCompleted || 0}</td>
                        <td style={tdNumStyle}>{p.avgQuizScore != null ? Number(p.avgQuizScore).toFixed(1) : '—'}</td>
                        <td style={tdNumStyle}>${Number(p.totalAiCostUsd || 0).toFixed(2)}</td>
                        <td style={tdNumStyle}>{p.totalAiPrompts || 0}</td>
                        <td style={tdNumStyle}>{p.engagementScore != null ? Number(p.engagementScore).toFixed(0) : '—'}</td>
                        <td style={tdNumStyle}>{p.daysSinceLastActivity != null ? Number(p.daysSinceLastActivity).toFixed(0) : '—'}</td>
                        <td style={tdStyle}>
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '.75rem',
                            fontWeight: 600,
                            background: (LEARNING_STYLE_COLORS[p.learningStyle] || '#6c757d') + '22',
                            color: LEARNING_STYLE_COLORS[p.learningStyle] || '#6c757d'
                          }}>
                            {p.learningStyle || '—'}
                          </span>
                        </td>
                        <td style={tdStyle}>{p.subscriptionStatus || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Pagination */}
              <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginTop: '1rem' }}>
                <button
                  onClick={() => setProfileFilters(prev => ({ ...prev, page: Math.max(0, prev.page - 1) }))}
                  disabled={profileFilters.page === 0}
                  style={pageBtnStyle}
                >
                  ← Previous
                </button>
                <span style={{ padding: '0.5rem', fontSize: '.85rem', color: 'var(--text-muted)' }}>
                  Page {profileFilters.page + 1}
                </span>
                <button
                  onClick={() => setProfileFilters(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={rows.length < 15}
                  style={pageBtnStyle}
                >
                  Next →
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  // ─── Main Render ──────────────────────────────────────────────────

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '1.5rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700 }}>Learning Intelligence</h1>
          <p style={{ margin: '0.25rem 0 0', fontSize: '.85rem', color: 'var(--text-muted)' }}>
            Advanced analytics powered by JSONB indexes and aggregation tables
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <DateRangePicker
            from={dateRange.from}
            to={dateRange.to}
            onChange={setDateRange}
          />
          <button onClick={loadTabData} style={refreshBtnStyle} title="Refresh data">
            ↻
          </button>
          <PdfExportButton onGenerate={handlePdfExport} disabled={loading} />
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0', borderBottom: '2px solid var(--border-light)', marginBottom: '1.5rem' }}>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '0.75rem 1.25rem',
              border: 'none',
              borderBottom: activeTab === tab.key ? '2px solid #007bff' : '2px solid transparent',
              background: 'transparent',
              color: activeTab === tab.key ? '#007bff' : 'var(--text-secondary)',
              fontWeight: activeTab === tab.key ? 600 : 400,
              cursor: 'pointer',
              fontSize: '.85rem',
              transition: 'all 0.2s',
              marginBottom: '-2px'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Error */}
      {error && (
        <div style={{
          padding: '1rem',
          background: '#dc354520',
          border: '1px solid #dc354560',
          borderRadius: '8px',
          marginBottom: '1rem',
          color: '#dc3545',
          fontSize: '.85rem'
        }}>
          Failed to load data: {error.message || 'Unknown error'}
          <button onClick={loadTabData} style={{ marginLeft: '1rem', ...clearBtnStyle }}>Retry</button>
        </div>
      )}

      {/* Tab content */}
      <div ref={dashboardRef}>
        {activeTab === 'funnel' && renderFunnel()}
        {activeTab === 'documents' && renderDocuments()}
        {activeTab === 'features' && renderFeatures()}
        {activeTab === 'sessions' && renderSessions()}
        {activeTab === 'profiles' && renderProfiles()}
      </div>
    </div>
  );
}

// ─── Shared Styles ────────────────────────────────────────────────

const cardStyle = {
  background: 'var(--card-bg)',
  border: '1px solid var(--border-light)',
  borderRadius: '12px',
  padding: '1.25rem',
  boxShadow: 'var(--shadow-sm)'
};

const chartTitleStyle = {
  margin: '0 0 1rem',
  fontSize: '1rem',
  fontWeight: 600
};

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '.8rem'
};

const thStyle = {
  padding: '0.5rem 0.75rem',
  textAlign: 'left',
  borderBottom: '2px solid var(--border-light)',
  fontSize: '.7rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--text-muted)',
  whiteSpace: 'nowrap'
};

const tdStyle = {
  padding: '0.5rem 0.75rem',
  borderBottom: '1px solid var(--border-light)',
  whiteSpace: 'nowrap'
};

const tdNumStyle = {
  ...tdStyle,
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums'
};

const inputStyle = {
  padding: '0.4rem 0.75rem',
  border: '1px solid var(--border-light)',
  borderRadius: '6px',
  background: 'var(--card-bg)',
  color: 'inherit',
  fontSize: '.85rem'
};

const clearBtnStyle = {
  padding: '0.4rem 0.75rem',
  border: '1px solid var(--border-light)',
  borderRadius: '6px',
  background: 'var(--card-bg)',
  color: 'inherit',
  cursor: 'pointer',
  fontSize: '.85rem'
};

const pageBtnStyle = {
  padding: '0.4rem 1rem',
  border: '1px solid var(--border-light)',
  borderRadius: '6px',
  background: 'var(--card-bg)',
  color: 'inherit',
  cursor: 'pointer',
  fontSize: '.8rem'
};

const refreshBtnStyle = {
  padding: '0.4rem 0.75rem',
  border: '1px solid var(--border-light)',
  borderRadius: '8px',
  background: 'var(--card-bg)',
  color: 'inherit',
  cursor: 'pointer',
  fontSize: '1.1rem',
  lineHeight: 1
};
